// Every card in base Cartographers (Jordy Adan, Thunderworks 2019), sourced
// rather than remembered:
//
// - Shapes come from the TTS mod's own Lua (workshop id 2123634085,
//   "Cartographers + Promos [Scripted]") - each explore card carries its
//   layout as a 4x4 boolean grid, parsed verbatim. docs/ has the reference.
// - Time values, coin flags, arrows and scoring text come from the card faces
//   in public/cards/ (sliced from the mod's sheets - the [NN/41] numbers below
//   are printed on the cards themselves).
// - Procedure comes from docs/rules.pdf (the official rulebook).
//
// Shapes are arrays of [row, col] cells. They are stored in ONE orientation;
// "The chosen shape can be rotated and/or flipped" (rules p6) is the placement
// code's job (board.js transforms), not the data's.
//
// The promo cards in the mod (four extra ambushes, the Skills deck) are
// deliberately absent: base game only, the Telestrations "the loop only"
// discipline.

export const TERRAINS = ['forest', 'village', 'farm', 'water', 'monster'];

const cells = (rows) => {
  const out = [];
  rows.forEach((row, r) => {
    row.split('').forEach((ch, c) => {
      if (ch === '#') out.push([r, c]);
    });
  });
  return out;
};

// The 13 explore cards (rules p3: "13 EXPLORE CARDS" = 11 terrain + 2 ruins).
// `shapes` lists the choices on the card; a shape with `coin: true` fills a
// coin symbol when chosen (rules p6). Card ids are the sheet slice filenames
// in public/cards/.
export const EXPLORE_CARDS = [
  {
    id: 110,
    name: 'Great River',
    time: 1,
    shapes: [
      { terrains: ['water'], cells: cells(['#', '#', '#']), coin: true },
      { terrains: ['water'], cells: cells(['..#', '.##', '##.']) },
    ],
  },
  {
    id: 111,
    name: 'Farmland',
    time: 1,
    shapes: [
      { terrains: ['farm'], cells: cells(['#', '#']), coin: true },
      { terrains: ['farm'], cells: cells(['.#.', '###', '.#.']) },
    ],
  },
  {
    id: 112,
    name: 'Hamlet',
    time: 1,
    shapes: [
      { terrains: ['village'], cells: cells(['#.', '##']), coin: true },
      { terrains: ['village'], cells: cells(['###', '##.']) },
    ],
  },
  {
    id: 113,
    name: 'Forgotten Forest',
    time: 1,
    shapes: [
      { terrains: ['forest'], cells: cells(['#.', '.#']), coin: true },
      { terrains: ['forest'], cells: cells(['#.', '##', '.#']) },
    ],
  },
  {
    id: 114,
    name: 'Hinterland Stream',
    time: 2,
    shapes: [
      { terrains: ['farm', 'water'], cells: cells(['###', '#..', '#..']) },
    ],
  },
  {
    id: 115,
    name: 'Homestead',
    time: 2,
    shapes: [
      { terrains: ['village', 'farm'], cells: cells(['#.', '##', '#.']) },
    ],
  },
  {
    id: 116,
    name: 'Orchard',
    time: 2,
    shapes: [
      { terrains: ['forest', 'farm'], cells: cells(['###', '..#']) },
    ],
  },
  {
    id: 117,
    name: 'Treetop Village',
    time: 2,
    shapes: [
      { terrains: ['forest', 'village'], cells: cells(['..##', '###.']) },
    ],
  },
  {
    id: 118,
    name: 'Marshlands',
    time: 2,
    shapes: [
      { terrains: ['forest', 'water'], cells: cells(['#..', '###', '#..']) },
    ],
  },
  {
    id: 119,
    name: 'Fishing Village',
    time: 2,
    shapes: [
      { terrains: ['village', 'water'], cells: cells(['####']) },
    ],
  },
  {
    id: 120,
    name: 'Rift Lands',
    time: 0,
    // "each player draws a 1x1 square anywhere on their map and fills it with
    // any terrain type shown on the card" (rules p7) - all five, monster
    // included.
    shapes: [
      { terrains: TERRAINS.slice(), cells: cells(['#']) },
    ],
  },
  // Ruins cards carry no shape and no time value; they constrain the NEXT
  // explore card to overlap a ruins space (rules p7).
  { id: 108, name: 'Temple Ruins', time: 0, ruins: true },
  { id: 109, name: 'Outpost Ruins', time: 0, ruins: true },
];

// The 4 base ambush cards. `direction` is the rotation arrow (which way map
// sheets are passed, rules p7); `corner` is the solo-mode corner grid in the
// card's top-right, recorded for completeness even though this app resolves
// ambushes with the multiplayer pass-the-map rule (bots hold real seats).
export const AMBUSH_CARDS = [
  {
    id: 100,
    name: 'Goblin Attack',
    direction: 'counterclockwise',
    corner: 'bottom-right',
    cells: cells(['#..', '.#.', '..#']),
  },
  {
    id: 101,
    name: 'Bugbear Assault',
    direction: 'clockwise',
    corner: 'top-right',
    cells: cells(['#.#', '#.#']),
  },
  {
    id: 102,
    name: 'Kobold Onslaught',
    direction: 'clockwise',
    corner: 'bottom-left',
    cells: cells(['#.', '##', '#.']),
  },
  {
    id: 103,
    name: 'Gnoll Raid',
    direction: 'counterclockwise',
    corner: 'top-left',
    cells: cells(['##', '#.', '##']),
  },
];

// Seasons, in play order, with time thresholds and which two edicts score
// (from the season card faces; also rules p8's spring example).
export const SEASONS = [
  { id: 400, name: 'Spring', threshold: 8, edicts: ['A', 'B'] },
  { id: 401, name: 'Summer', threshold: 8, edicts: ['B', 'C'] },
  { id: 402, name: 'Fall', threshold: 7, edicts: ['C', 'D'] },
  { id: 403, name: 'Winter', threshold: 6, edicts: ['D', 'A'] },
];

// The 16 scoring cards in their four back-matched families (rules p4 setup:
// one drawn per family, then dealt below the edicts). `fn` names the
// evaluator in scoring.js; text is the card's own, kept because the UI shows
// it and the tests quote it.
export const SCORING_FAMILIES = [
  {
    family: 'forests',
    cards: [
      { id: 900, key: 'sentinel-wood', name: 'Sentinel Wood', text: 'Earn one reputation star for each forest space adjacent to the edge of the map.' },
      { id: 901, key: 'greenbough', name: 'Greenbough', text: 'Earn one reputation star for each row and column with at least one forest space. The same forest space may be scored in a row and a column.' },
      { id: 902, key: 'treetower', name: 'Treetower', text: 'Earn one reputation star for each forest space surrounded on all four sides by filled spaces or the edge of the map.' },
      { id: 903, key: 'stoneside-forest', name: 'Stoneside Forest', text: 'Earn three reputation stars for each mountain space connected to another mountain space by a cluster of forest spaces.' },
    ],
  },
  {
    family: 'farm-and-sea',
    cards: [
      { id: 500, key: 'canal-lake', name: 'Canal Lake', text: 'Earn one reputation star for each water space adjacent to at least one farm space. Earn one reputation star for each farm space adjacent to at least one water space.' },
      { id: 501, key: 'mages-valley', name: 'Mages Valley', text: 'Earn two reputation stars for each water space adjacent to a mountain space. Earn one reputation star for each farm space adjacent to a mountain space.' },
      { id: 502, key: 'golden-granary', name: 'The Golden Granary', text: 'Earn one reputation star for each water space adjacent to a ruins space. Earn three reputation stars for each farm space on a ruins space.' },
      { id: 503, key: 'shoreside-expanse', name: 'Shoreside Expanse', text: 'Earn three reputation stars for each cluster of farm spaces not adjacent to a water space or the edge of the map. Earn three reputation stars for each cluster of water spaces not adjacent to a farm space or the edge of the map.' },
    ],
  },
  {
    family: 'villages',
    cards: [
      { id: 600, key: 'wildholds', name: 'Wildholds', text: 'Earn eight reputation stars for each cluster of six or more village spaces.' },
      { id: 601, key: 'great-city', name: 'Great City', text: 'Earn one reputation star for each village space in the largest cluster of village spaces that is not adjacent to a mountain space.' },
      { id: 602, key: 'greengold-plains', name: 'Greengold Plains', text: 'Earn three reputation stars for each cluster of village spaces that is adjacent to three or more different terrain types.' },
      { id: 603, key: 'shieldgate', name: 'Shieldgate', text: 'Earn two reputation stars for each village space in the second largest cluster of village spaces.' },
    ],
  },
  {
    family: 'spatial',
    cards: [
      { id: 700, key: 'borderlands', name: 'Borderlands', text: 'Earn six reputation stars for each complete row or complete column of filled spaces.' },
      { id: 701, key: 'lost-barony', name: 'Lost Barony', text: 'Earn three reputation stars for each space along one edge of the largest square of filled spaces.' },
      { id: 702, key: 'broken-road', name: 'The Broken Road', text: 'Earn three reputation stars for each complete diagonal line of filled spaces that touches the left and bottom edges of the map.' },
      { id: 703, key: 'cauldrons', name: 'The Cauldrons', text: 'Earn one reputation star for each empty space surrounded on all four sides by filled spaces or the edge of the map.' },
    ],
  },
];

export const cardById = (id) => EXPLORE_CARDS.find((c) => c.id === id)
  || AMBUSH_CARDS.find((c) => c.id === id)
  || null;

export const scoringCardByKey = (key) => {
  for (const fam of SCORING_FAMILIES) {
    const hit = fam.cards.find((c) => c.key === key);
    if (hit) return hit;
  }
  return null;
};
